// src/App.jsx
import AboutSection from "./components/AboutSection";
import { BrowserRouter as Router, Routes, Route, Link, NavLink } from "react-router-dom";
import NumberHunt from "./pages/NumberHunt";
import HowItWorks from "./pages/HowItWorks";
import logo from "./assets/Updated _Once A Hunt_ logo.png";

/* =========================
   MAGNIFIER (FINAL)
   ========================= */
function MindMagnifierInline() {
  return (
    <span className="relative inline-flex items-baseline">
      {/* LENS BODY */}
      <span
        className="
          relative z-20 inline-flex items-center justify-center
          w-[160px] h-[160px]
          sm:w-[180px] sm:h-[180px]
          rounded-full
          bg-white/60
          overflow-hidden
        "
      >
        {/* GLASS SHINE */}
        <span
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(
                120px 90px at 30% 28%,
                rgba(255,255,255,0.95) 0%,
                rgba(255,255,255,0.55) 30%,
                rgba(255,255,255,0.15) 55%,
                rgba(255,255,255,0) 72%
              )
            `,
          }}
        />

        {/* secondary reflection */}
        <span
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(90px 70px at 70% 70%, rgba(255,255,255,0.25), rgba(255,255,255,0) 65%)",
          }}
        />

        {/* inner depth */}
        <span
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{
            boxShadow: "inset 0 18px 30px rgba(0,0,0,0.12)",
          }}
        />

        {/* WORD */}
        <span
          className="relative z-30 font-extrabold text-brand-primary leading-none text-5xl sm:text-6xl md:text-7xl"
          style={{
            fontFamily: "var(--font-head)",
            transform: "translateY(-2px)",
          }}
        >
          Mind
        </span>
      </span>

      {/* OUTER RING */}
      <span
        className="
          absolute z-40
          w-[160px] h-[160px]
          sm:w-[180px] sm:h-[180px]
          rounded-full
          border-[10px] border-black
          left-0 top-0
          pointer-events-none
        "
        aria-hidden="true"
      />

      {/* HANDLE — attached to edge */}
      <span
        className="absolute z-40 bg-black pointer-events-none"
        style={{
          width: "96px",
          height: "18px",
          borderRadius: "999px",
          left: "50%",
          top: "50%",
          transformOrigin: "0% 50%",
          transform: "rotate(35deg) translateX(80px)",
        }}
        aria-hidden="true"
      />
    </span>
  );
}

/* =========================
   NAV LINK
   ========================= */
function TopBarLink({ to, children }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        [
          "px-4 py-2 rounded-full text-sm sm:text-base font-extrabold transition",
          isActive
            ? "bg-white/90 text-black"
            : "text-black/80 hover:bg-white/70 hover:text-black",
        ].join(" ")
      }
      style={{ fontFamily: "var(--font-head)" }}
    >
      {children}
    </NavLink>
  );
}

/* =========================
   PAGES
   ========================= */
function About() {
  return <AboutSection />;
}

function Contact() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-16">
      <h1 className="text-4xl font-extrabold text-brand-primary">Contact Us</h1>
      <p className="text-brand-muted mt-4">Coming soon.</p>
    </div>
  );
}

function Login() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-16">
      <h1 className="text-4xl font-extrabold text-brand-primary">
        Log in / Sign up
      </h1>
      <p className="text-brand-muted mt-4">Coming soon.</p>
    </div>
  );
}

/* =========================
   HOME (UNCHANGED)
   ========================= */
function Home() {
  const gallery = [
    { src: "/gallery/gallery-12.jpg", alt: "Kids reading a map outdoors" },
    { src: "/gallery/gallery-11.jpg", alt: "Science exploration with magnifier" },
    { src: "/gallery/gallery-13.webp", alt: "Outdoor discovery and teamwork" },
    { src: "/gallery/gallery-05.jpg", alt: "Hands-on puzzles and problem solving" },
    { src: "/gallery/gallery-15.webp", alt: "Math games with letter cards" },
    { src: "/gallery/gallery-06.jpg", alt: "Learning numbers through play" },
    { src: "/gallery/gallery-16.webp", alt: "Team mission outdoors" },
    { src: "/gallery/gallery-02.jpg", alt: "Finding clues with a magnifier" },
  ];

  return (
    <div className="space-y-24">
      {/* HERO */}
      <section className="hero-sky">
        <div className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT */}
          <div className="text-center lg:text-left">
            {/* MOTO */}
            <div
              className="font-extrabold text-brand-primary leading-tight"
              style={{ fontFamily: "var(--font-head)" }}
            >
              <div className="text-2xl sm:text-3xl md:text-4xl">
                Play the Hunt
              </div>

              <div className="mt-3 flex items-baseline justify-center lg:justify-start gap-3 text-2xl sm:text-3xl md:text-4xl">
                <span>Grow the</span>
                <MindMagnifierInline />
              </div>
            </div>

            <p className="mt-6 text-lg sm:text-xl text-brand-muted max-w-xl">
              Once-A-Hunt turns learning into an adventure through playful scavenger hunts
              designed to spark curiosity, movement, and discovery.
            </p>

            <div className="mt-8">
              <Link
                to="/number-hunt"
                className="inline-flex items-center rounded-full px-8 py-4 font-extrabold text-white bg-brand-accent hover:bg-brand-accentDark transition shadow"
                style={{ fontFamily: "var(--font-head)" }}
              >
                Create a scavenger hunt
              </Link>
            </div>
          </div>

          {/* RIGHT IMAGE */}
          <div className="relative">
            <div className="rounded-[28px] overflow-hidden shadow-lg bg-white">
              <img
                src="/hero-cats.jpg"
                alt="Detective cats with magnifiers"
                className="w-full h-[340px] sm:h-[420px] object-cover"
              />
            </div>
            <div className="mt-4 text-center hero-caption text-lg">
              A cozy mystery vibe for curious kids 🐾
            </div>
          </div>
        </div>
      </section>

      {/* THEMES */}
      <section className="bg-white/70">
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h2 className="text-4xl font-extrabold text-brand-primary mb-10">
            Scavenger hunt themes
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "🌍", title: "Around the World", desc: "Maps, cultures, and exploration." },
              { icon: "🔢", title: "Math", desc: "Numbers, logic, and puzzles." },
              { icon: "🔬", title: "Science", desc: "Observation and discovery." },
              { icon: "✨", title: "Mixed Adventure", desc: "A playful blend of all themes." },
            ].map((t) => (
              <div key={t.title} className="bg-white rounded-2xl p-6 shadow border">
                <div className="text-3xl mb-3">{t.icon}</div>
                <div className="font-extrabold">{t.title}</div>
                <div className="text-brand-muted mt-2">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h2 className="text-4xl font-extrabold text-brand-primary mb-4">
            How it works
          </h2>
          <p className="text-brand-muted mb-12">
            Simple for grown-ups. Magical for kids.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "1", t: "Choose a theme", d: "Pick Math, Science, or a mix." },
              { n: "2", t: "We build the hunt", d: "Printable clues and puzzles." },
              { n: "3", t: "Explore & learn", d: "Kids move, solve, and discover." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-6 shadow border">
                <div className="text-3xl font-extrabold mb-2">{s.n}</div>
                <div className="font-extrabold mb-2">{s.t}</div>
                <p className="text-brand-muted">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section>
        <div className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-4xl font-extrabold text-brand-primary text-center mb-10">
            Learning in action
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((g) => (
              <img
                key={g.src}
                src={g.src}
                alt={g.alt}
                className="rounded-2xl object-cover w-full h-56"
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* =========================
   APP
   ========================= */
export default function App() {
  return (
    <Router>
      <div className="min-h-screen">
        <header className="sticky top-0 z-50">
          {/* LOGO ROW ✅ ONLY CHANGE: SHORTER VERTICALLY */}
          <div className="bg-white/90 backdrop-blur border-b">
            {/* ✅ CHANGED: py-3 -> py-1 (less height) */}
            <div className="max-w-7xl mx-auto px-4 py-1 flex items-center gap-4">
              {/* ✅ Logo + Name link to HOME (unchanged behavior) */}
              <Link to="/" className="flex items-center gap-4">
                <img
                  src={logo}
                  alt="Once-A-Hunt logo"
                  /* ✅ CHANGED: h-36 -> h-24 (shorter logo row) */
                  className="h-24 w-auto object-contain"
                />
                <div className="leading-tight">
                  {/* ✅ CHANGED: text-3xl -> text-2xl (matches smaller header) */}
                  <div className="text-2xl font-extrabold text-brand-primary">
                    Once-A-Hunt
                  </div>
                  {/* ✅ CHANGED: text-sm -> text-xs */}
                  <div className="text-xs text-brand-muted">
                    A playful learning adventure
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* GREEN BAR (unchanged) */}
          <div className="wix-green-bar">
            <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-end gap-3">
              <TopBarLink to="/about">About us</TopBarLink>
              <TopBarLink to="/how-it-works">How it works</TopBarLink>
              <TopBarLink to="/contact">Contact us</TopBarLink>
              <TopBarLink to="/login">Log in / Sign up</TopBarLink>
            </div>
          </div>
        </header>

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/number-hunt" element={<NumberHunt />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </div>
    </Router>
  );
}
